import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { LoginResponse } from '@/services/authService.ts';
import { authHandler } from '@/utils/authHandler';
import { errorTranslator } from '@/utils/errorTranslator';

const runtimeApiBaseUrl = typeof window !== 'undefined' ? window.__APP_CONFIG__?.apiBaseUrl : undefined;
const API_BASE_URL = runtimeApiBaseUrl || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';

export interface ApiError {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  path: string;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  page: number;
  size: number;
  totalPages: number;
}

export class ApiClientError extends Error {
  status?: number;
  data?: ApiError;

  constructor(message: string, status?: number, data?: ApiError) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.data = data;
  }
}

export interface QueryParams {
  _page?: number;
  _size?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  [key: string]: unknown;
}

/**
 * Centralized API client for the TV Streaming Admin application
 * Handles authentication, error handling, and common request patterns
 */
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}`,
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': localStorage.getItem('language') || 'es'
      },
      // Important: Include HTTP-only cookies for authentication
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Log request for debugging (remove in production)
        if (process.env.NODE_ENV === 'development') {
          console.debug(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
            params: config.params,
            data: config.data,
          });
        }
        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        if (process.env.NODE_ENV === 'development') {
          console.debug(`API Response: ${response.status}`, response.data);
        }
        return response;
      },
      async (error) => {
        if (error.response) {
          const apiError: ApiError = error.response.data;

          if (error.response.status === 401 && !error.config?.url?.includes('/auth/logout')) {
            console.warn('Authentication failed - token expired, triggering logout');
            try {
              await authHandler.triggerLogout();
            } catch (logoutError) {
              console.error('Error during logout:', logoutError);
            }
          }

          console.error('API Error:', {
            status: error.response.status,
            message: apiError.message || error.message,
            path: apiError.path,
          });

          // Use error translator to get localized error message
          const translatedError = errorTranslator.translateError({
            status: error.response.status,
            message: apiError.message,
          });

          throw new ApiClientError(translatedError, error.response.status, apiError);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          // Use error translator for network errors
          const translatedError = errorTranslator.translateError({ type: 'network' });
          throw new ApiClientError(translatedError);
        } else {
          console.error('Request Error:', error.message);
          // Use error translator for unexpected errors
          const translatedError = errorTranslator.translateError({});
          throw new ApiClientError(translatedError);
        }
      }
    );
  }

  // Generic HTTP methods
  async get<T>(endpoint: string, params?: QueryParams): Promise<T> {
    const response = await this.client.get<T>(endpoint, { params });
    return response.data;
  }

  async post<T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(endpoint, data, config);
    return response.data;
  }

  async put<T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(endpoint, data, config);
    return response.data;
  }

  async patch<T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(endpoint, data, config);
    return response.data;
  }

  async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(endpoint, config);
    return response.data;
  }

  // Specialized methods for common patterns

  /**
   * Get paginated data with common query parameters
   */
  async getPaginated<T>(endpoint: string, params?: QueryParams): Promise<PaginatedResponse<T>> {
    return this.get<PaginatedResponse<T>>(endpoint, params);
  }

  /**
   * Create a resource and return the created item
   */
  async create<TRequest, TResponse>(endpoint: string, data: TRequest): Promise<TResponse> {
    return this.post<TResponse>(endpoint, data);
  }

  /**
   * Update a resource by ID
   */
  async update<TRequest, TResponse>(endpoint: string, id: string, data: TRequest): Promise<TResponse> {
    return this.put<TResponse>(`${endpoint}/${id}`, data);
  }

  /**
   * Partially update a resource by ID
   */
  async partialUpdate<TRequest, TResponse>(endpoint: string, id: string, data: Partial<TRequest>): Promise<TResponse> {
    return this.patch<TResponse>(`${endpoint}/${id}`, data);
  }

  /**
   * Delete a resource by ID
   */
  async deleteById<TResponse = { success: boolean; message: string }>(endpoint: string, id: string | number): Promise<TResponse> {
    return this.delete<TResponse>(`${endpoint}/${id}`);
  }

  /**
   * Get a single resource by ID
   */
  async getById<T>(endpoint: string, id: string): Promise<T> {
    return this.get<T>(`${endpoint}/${id}`);
  }

  /**
   * Toggle status of a resource (common pattern for activate/deactivate)
   */
  async toggleStatus<TResponse>(endpoint: string, id: string, status: boolean): Promise<TResponse> {
    return this.patch<TResponse>(`${endpoint}/${id}/status`, { active: status });
  }

  // Authentication-specific methods (for services that need direct access)

  /**
   * Login with credentials (uses different base path)
   */
  async login(credentials: { username: string; password: string }): Promise<LoginResponse> {
    return this.post('/auth/login', credentials);
  }

  /**
   * Logout (uses different base path)
   */
  async logout() {
    return this.post('/auth/logout');
  }

  /**
   * Refresh authentication token (uses different base path)
   */
  async refreshToken(): Promise<LoginResponse> {
    return this.post('/auth/refresh');
  }

  /**
   * Get the underlying axios instance for advanced usage
   */
  getInstance(): AxiosInstance {
    return this.client;
  }
}

// Create and export a singleton instance
export const apiClient = new ApiClient();

// Export the class for testing or creating additional instances
export { ApiClient };
